/**
 * Tableau de bord partenaire (BET, contrôleur technique, SPS, OPC…).
 *
 * RP83 (COL-A6, BET-R7) — le bloc des tâches assignées (ancienne table `taches`, 5 premiers
 * projets) est retiré : les actions sont dans « Mes actions ». À la place, la version
 * partenaire de « Ma semaine » (GET /api/me/ma-semaine, limitée à ses chantiers) :
 * « À viser » (avec échéance), « Lots à viser » et « Lots à rédiger » (Fluidité B2),
 * réunions de la semaine.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from '@phosphor-icons/react';
import { Skeleton } from '../../components/ui/skeleton';
import MesVisasCard from '../project/visas/MesVisasCard';
import MaSemaineBloc from './MaSemaineBloc';
import LotsPartenaire, { lotsAViser } from './LotsPartenaire';
import { LOTS_PARTENAIRE } from '../../lib/libelles';
import { URL_MA_SEMAINE } from './maSemaine';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardIntervenant({ user }) {
  const [chantiers, setChantiers] = useState([]);
  const [semaine, setSemaine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let annule = false;
    Promise.all([
      axios.get(`${API_URL}/api/chantiers`, { withCredentials: true }).then((r) => r.data).catch(() => []),
      axios.get(`${API_URL}${URL_MA_SEMAINE}`, { withCredentials: true }).then((r) => r.data).catch(() => null),
    ]).then(([ch, sem]) => {
      if (annule) return;
      setChantiers(Array.isArray(ch) ? ch : []);
      setSemaine(sem);
      setLoading(false);
    });
    return () => { annule = true; };
  }, []);

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;

  const aViser = semaine?.a_viser || [];
  const enRetard = aViser.filter((v) => v.en_retard);
  const mesLots = semaine?.mes_lots || [];
  const reunions = semaine?.reunions || [];

  return (
    <div className="space-y-6" data-testid="dashboard-intervenant">
      <div>
        <h1 className="text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: "'DM Serif Display', serif" }}>
          Bonjour {user.name.split(' ')[0]}
        </h1>
        <p className="text-sm text-[#6B6B6B]">Ma semaine : pièces à viser, lots à viser ou à rédiger et réunions</p>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#1E3A5F]">{chantiers.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Chantiers</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4" data-testid="partenaire-compteur-a-viser">
          <p className="text-2xl font-bold text-[#2C2C2C]">{semaine?.compteurs?.a_viser ?? aViser.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">À viser</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#C75B39]">{enRetard.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Visas en retard</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#2D6A4F]">{lotsAViser(mesLots).length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">{LOTS_PARTENAIRE.aViser}</p>
        </div>
      </div>

      {/* RP52-54 / RP83 — À viser (échéance, retard) et visas déposés */}
      <MesVisasCard />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Fluidité B2 (P3#30) — « Lots à viser » et « Lots à rédiger » (ex-« Mes lots ») */}
        <LotsPartenaire mesLots={mesLots} />

        {/* Réunions de la semaine */}
        <MaSemaineBloc cle="reunions" items={reunions} total={semaine?.compteurs?.reunions} vide="Aucune réunion cette semaine" />
      </div>

      {/* Mes projets (tous, sans limite) */}
      <div className="border border-[#E0E0DB] bg-white">
        <div className="px-4 py-3 border-b border-[#E0E0DB]">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Mes chantiers</h2>
        </div>
        <div className="divide-y divide-[#E0E0DB]">
          {chantiers.length === 0 && <p className="text-sm text-[#9A9A9A] text-center py-6">Aucun chantier</p>}
          {chantiers.map(c => (
            <Link key={c.id} to={`/chantiers/${c.id}`} className="block px-4 py-3 hover:bg-[#FAFAF9] transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2C2C2C]">{c.name}</p>
                  <p className="text-xs text-[#6B6B6B]">{c.address}</p>
                </div>
                <ArrowRight size={14} className="text-[#9A9A9A]" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
